import React, { useEffect, useState, useCallback, useRef } from "react";
import Input from "../../components/controls/input/customInput/CustomInput";
import purchaseOrdersService from "../../store/services/purchaseOrders.service";
import { CalendarIcon } from "../../modules/default-svg";
import _, { debounce } from "lodash";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import {
  calculatePageSize,
  getLabelByType,
  purchaseOrderStatusOptions,
} from "../../helpers/utils";
import Table from "../../components/controls/tables/Table";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import {
  getAccountCurrency,
  displayDecimalFormatted,
} from "../../utils/systemSettings";
import Skeleton from "react-loading-skeleton";
import {
  getPurchaseOrdersRequest,
  createPurchaseOrderRequest,
  updatePurchaseOrderRequest,
  deletePurchaseOrderRequest,
} from "../../store/actions/purchaseOrders.actions";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import searchService from "../../store/services/search.service";
import Searchable from "../../components/controls/select/CustomSelect";
import AssociatedInvoices from "./AssociatedInvoices";

const PurchaseOrders = (props) => {
  const { projectID, allPurchaseOrders, loading, pagination, pageLoading } =
    props;

  const settings = JSON.parse(localStorage.getItem("settings"));
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [perPageItems] = useState(calculatePageSize());
  const [pageNum, setPageNum] = useState(1);
  const [selectedRow, setSelectedRow] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [isCreateNewRow, setIsCreateNewRow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeletedId] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);
  const [formErrors, setFormErrors] = useState({
    poId: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [invoiceEntries, setInvoiceEntries] = useState([]);
  const [purchaseOrderDto, setPurchaseOrderDto] = useState({
    vendor: "",
    poId: "",
    pr: "",
    value: 0,
    paid: 0,
    accural: 0,
    outstandingValue: 0,
    createdAt: null,
    endAt: null,
    status: 0,
    comments: "",
    description: "",
    changeRequest: null
  });
  const [purchaseOrderUpdateDto, setPurchaseOrderUpdateDto] = useState({
    vendor: "",
    poId: "",
    pr: "",
    description: "",
    value: 0,
    createdAt: null,
    endAt: null,
    status: 0,
    comments: "",
    description: "",
    attachments: [],
  });
  const [purchaseOrderCreateDto, setPurchaseOrderCreateDto] = useState({
    vendor: "",
    poId: "",
    pr: "",
    description: "",
    value: 0,
    createdAt: null,
    endAt: null,
    status: 0,
    comments: "",
    projectId: projectID,
    changeRequestId: null
  });
  const [tableSearch, setTableSearch] = useState(null);

  const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch = false) => {
    const payload = { page: pageNumber ?? pageNum, size: pageSize ?? perPageItems, search: forceClearSearch ? null : (searchTerm ?? tableSearch) };
    payload.parent = "Project";

    if (projectID) {
      payload.id = projectID;
      payload.parent = "Project";
    }
    else {
      setData([]);
      return;
    }

    dispatch(getPurchaseOrdersRequest(payload));
  };
  const handleAddRow = useCallback(() => {
    setIsCreateNewRow(true);
    fetchData(pagination.TotalPages ?? 1, perPageItems > 10 ? perPageItems - 5 : perPageItems - 3);
  }, []);

  const handleDelete = (selectedRow) => {
    const id = selectedRow.id;
    dispatch(deletePurchaseOrderRequest({ projectId: projectID, id: id }));
    setDeletedId(id);
  };

  const initDataState = () => {
    setFormErrors({});
    setPurchaseOrderDto({
      vendor: "",
      poId: "",
      pr: "",
      value: 0,
      paid: 0,
      accural: 0,
      outstandingValue: 0,
      createdAt: null,
      endAt: null,
      status: 0,
      comments: "",
      description: "",
      changeRequest: null
    });
    setPurchaseOrderCreateDto({
      vendor: "",
      poId: "",
      pr: "",
      description: "",
      value: 0,
      createdAt: null,
      endAt: null,
      status: 0,
      comments: "",
      projectId: projectID,
      changeRequestId: null
    });
    setPurchaseOrderUpdateDto({
      vendor: "",
      poId: "",
      pr: "",
      description: "",
      value: 0,
      createdAt: null,
      endAt: null,
      status: 0,
      comments: "",
    });
    setAttachments([]);
    setInvoiceEntries([]);
    setChangeRequests([]);
  };

  const handleCreate = (val) => {
    setIsEdit(false);
    initDataState();
    setIsCreate(val);
  };

  const handleSave = () => {
    const isEdit = Object.keys(selectedRow).length > 0;

    const errors = {};
    if (isEdit ? !purchaseOrderUpdateDto.poId : !purchaseOrderCreateDto.poId)
      errors["poId"] = `Required`;

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (isEdit) {
      dispatch(
        updatePurchaseOrderRequest({
          id: selectedRow.id,
          projectId: projectID,
          payload: purchaseOrderUpdateDto,
        })
      );
      return;
    } else {
      dispatch(
        createPurchaseOrderRequest({
          projectId: projectID,
          payload: purchaseOrderCreateDto,
        })
      );
    }
  };

  const fetchPurchaseOrderData = async () => {
    setIsLoading(true);
    purchaseOrdersService
      .fetchSinglePurchaseOrderRequest(selectedRow.id)
      .then((data) => {
        setPurchaseOrderDto({
          vendor: data.vendor,
          poId: data.poId,
          pr: data.pr,
          value: data.value,
          paid: data.paid,
          accural: data.accural,
          outstandingValue: data.outstandingValue,
          createdAt: data.createdAt,
          endAt: data.endAt,
          status: data.status,
          comments: data.comments,
          description: data.description,
          changeRequest: data.changeRequest ? { label: data.changeRequest.name, value: data.changeRequest.id } : null
        });
        setAttachments(data.attachments || []);
        setInvoiceEntries(data.invoiceEntries || []);
        setPurchaseOrderUpdateDto({
          vendor: data.vendor,
          poId: data.poId,
          pr: data.pr,
          description: data.description,
          value: data.value,
          createdAt: data.createdAt,
          endAt: data.endAt,
          status: data.status,
          comments: data.comments,
        });
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title:
              "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    setIsEdit(false);
    initDataState();
  };

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0) {
      initDataState();
      isEdit && fetchPurchaseOrderData();
    }
  }, [selectedRow, isEdit]);

  useEffect(() => {
    fetchData();
  }, [pageNum, perPageItems]);

  const handleCancelAddRow = () => {
    setIsCreateNewRow(false);
    fetchData();
  };

  const changRequestSearchdebounced = useRef(
    debounce((value, params) => searchChangRequestHandler(value, params), 500)
  );

  const changRequestSearch = useCallback((value, params) => {
    changRequestSearchdebounced.current(value, params);
  }, []);

  const searchChangRequestHandler = (value, params) => {
    if (!params.projectId) {
      setChangeRequests([]);
      return;
    }
    searchService
      .ChangeRequests(value, params)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setChangeRequests(transformedArray);
      })
      .catch(() => {
        setChangeRequests([]);
      });
  };

  const handleSearch = (filteredData) => {
    setData(filteredData);
  };
  useEffect(() => {
    const outstandingValue =
      purchaseOrderDto.value - purchaseOrderDto.paid - purchaseOrderDto.accural;
    setPurchaseOrderDto((prev) => ({
      ...prev,
      ["outstandingValue"]: outstandingValue,
    }));
  }, [purchaseOrderDto.value]);

  useEffect(() => {
    if (allPurchaseOrders && allPurchaseOrders.length > 0) {
      if (isCreateNewRow) {
        setData(allPurchaseOrders.sort((a, b) => b.isFlagged - a.isFlagged));
        setIsCreateNewRow(false);
      } else {
        if (pageNum === 1) {
          setData(
            allPurchaseOrders
              .filter((item) => item.id !== deleteId)
              .sort((a, b) => {
                return b.isFlagged - a.isFlagged;
              })
          );
        }
        else {
          setData((prevData) => {
            const newMap = new Map(
              allPurchaseOrders.map((item) => [item.id, item])
            );

            const updatedPrevData = prevData.map((item) =>
              newMap.has(item.id) ? newMap.get(item.id) : item
            );

            const prevDataIds = new Set(prevData.map((item) => item.id));
            const newItems = allPurchaseOrders.filter(
              (shift) => !prevDataIds.has(shift.id)
            );

            return [...updatedPrevData, ...newItems]
              .filter((item) => item.id !== deleteId)
              .sort((a, b) => b.isFlagged - a.isFlagged);
          });
        }
      }
    } else if (allPurchaseOrders && allPurchaseOrders.length === 0) {
      setData([]);
    }
  }, [allPurchaseOrders]);

  const handleInputChange = (name, value) => {
    if (isEdit) {
      setPurchaseOrderUpdateDto((prev) => ({ ...prev, [name]: value }));
    } else {
      setPurchaseOrderCreateDto((prev) => ({ ...prev, [name]: value }));
    }
    setPurchaseOrderDto((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeRequestChange = (newCR) => {
    if (!isEdit) {
      setPurchaseOrderCreateDto((prev) => ({ ...prev, ["changeRequestId"]: newCR ? newCR.value : null }));
    }
    setPurchaseOrderDto((prev) => ({ ...prev, ["changeRequest"]: newCR }));
  };

  const formBody = () => {
    return (
      <>
        <div className="d-flex flex-col">
          <div className="row w-100 mb-3 gx-3">
            <div className={`${!isEdit || purchaseOrderDto.changeRequest ? 'col-3' : 'col-4'}`}>
              <Input
                inputwd=""
                icon={false}
                placeholder="Enter Vendor"
                value={purchaseOrderDto.vendor}
                type="text"
                handleChange={(value) => {
                  handleInputChange("vendor", value);
                }}
                label="Vendor"
                loading={isLoading}
              />
            </div>
            <div className={`${!isEdit || purchaseOrderDto.changeRequest ? 'col-3' : 'col-4'}`}>
              <Input
                inputwd=""
                icon={false}
                placeholder="Enter PR"
                value={purchaseOrderDto.pr}
                type="text"
                handleChange={(value) => {
                  handleInputChange("pr", value);
                }}
                label="PR"
                loading={isLoading}
              />
            </div>
            <div className={`${!isEdit || purchaseOrderDto.changeRequest ? 'col-3' : 'col-4'}`}>
              <Input
                inputwd=""
                icon={false}
                placeholder="Enter PO #"
                value={purchaseOrderDto.poId}
                type="text"
                handleChange={(value) => {
                  handleInputChange("poId", value);
                }}
                label="PO #"
                isRequired
                errorMessage={formErrors.poId}
                loading={isLoading}
              />
            </div>
            {
              isEdit && purchaseOrderDto.changeRequest &&
              (
                <div className="col-3">
                  <label class="Input_label">Change Request</label>
                  {isLoading ? (
                    <Skeleton
                      height={40}
                      baseColor="#e0e0e0"
                      highlightColor="#ECE7FD"
                    />
                  ) : (
                    <p
                      className=" text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md"
                      style={{ border: "1px solid #342C82" }}
                    >
                      {purchaseOrderDto.changeRequest.label}
                    </p>
                  )}
                </div>
              )
            }
            {
              !isEdit &&
              (
                <div className="col-3">
                  <Searchable
                    label="Change Request"
                    searchText={(e) =>
                      changRequestSearch(e, {
                        projectId: projectID,
                      })
                    }
                    SelectText="change request"
                    options={changeRequests}
                    defaultVal={purchaseOrderDto.changeRequest}
                    onChange={(value) => {
                      handleChangeRequestChange(value);
                    }}
                    loading={isLoading}
                  />
                </div>
              )
            }
          </div>
          <div className="row align-items-end w-100 mb-3 gx-3">
            <div className="col-4">
              <DropDown
                label="Status"
                options={purchaseOrderStatusOptions}
                defaultVal={getLabelByType(
                  "purchaseOrderStatus",
                  purchaseOrderDto.status
                )}
                onChange={(value) => {
                  handleInputChange("status", value);
                }}
                loading={isLoading}
              />
            </div>
            <div className="col-4">
              <Input
                icon={<CalendarIcon />}
                value={purchaseOrderDto.createdAt}
                maxDate={purchaseOrderDto.endAt}
                type="date"
                handleChange={(value) => {
                  handleInputChange("createdAt", value);
                }}
                label="Created Date"
                loading={isLoading}
              />
            </div>
            <div className="col-4">
              <Input
                icon={<CalendarIcon />}
                value={purchaseOrderDto.endAt}
                minDate={purchaseOrderDto.createdAt}
                type="date"
                handleChange={(value) => {
                  handleInputChange("endAt", value);
                }}
                label="End Date"
                loading={isLoading}
              />
            </div>
          </div>
          <div className="row w-100 align-items-center mb-3 gx-3">
            <div className="col-3">
              <Input
                inputwd=""
                icon={false}
                label="Value"
                placeholder="0"
                value={purchaseOrderDto.value}
                type="number"
                handleChange={(value) => {
                  handleInputChange("value", value);
                }}
                currency={true}
                loading={isLoading}
              />
            </div>
            {isEdit && (
              <>
                <div className="col-3">
                  <label class="Input_label">Paid</label>
                  {isLoading ? (
                    <Skeleton
                      height={40}
                      baseColor="#e0e0e0"
                      highlightColor="#ECE7FD"
                    />
                  ) : (
                    <p
                      className=" text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md"
                      style={{ border: "1px solid #342C82" }}
                    >
                      {`${getAccountCurrency(
                        settings
                      )} ${displayDecimalFormatted(
                        settings,
                        purchaseOrderDto.paid
                      )}`}
                    </p>
                  )}
                </div>
                <div className="col-3">
                  <label class="Input_label">Accural</label>
                  {isLoading ? (
                    <Skeleton
                      height={40}
                      baseColor="#e0e0e0"
                      highlightColor="#ECE7FD"
                    />
                  ) : (
                    <p
                      className=" text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md"
                      style={{ border: "1px solid #342C82" }}
                    >
                      {`${getAccountCurrency(
                        settings
                      )} ${displayDecimalFormatted(
                        settings,
                        purchaseOrderDto.accural
                      )}`}
                    </p>
                  )}
                </div>
                <div className="col-3">
                  <label class="Input_label">Outstanding Value</label>
                  {isLoading ? (
                    <Skeleton
                      height={40}
                      baseColor="#e0e0e0"
                      highlightColor="#ECE7FD"
                    />
                  ) : (
                    <p
                      className={`text-[#fff] text-center py-[9px] rounded-md ${purchaseOrderDto.outstandingValue < 0
                        ? "bg-[#da3b01]"
                        : "bg-[#107c10]"
                        }`}
                      style={{ border: `1px solid #342C82` }}
                    >
                      {`${getAccountCurrency(
                        settings
                      )} ${displayDecimalFormatted(
                        settings,
                        purchaseOrderDto.outstandingValue
                      )}`}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="mb-4">
            {isEdit && <AssociatedInvoices invoices={invoiceEntries} isLoading={isLoading} />}
          </div>

          <div className="row w-100 mb-3 gx-3">
            <RichTextEditor
              label="Description"
              content={purchaseOrderDto.description}
              onChange={(value) => {
                handleInputChange("description", value);
              }}
              loading={isLoading}
            />
          </div>
          <div className="row w-100 mb-3 gx-3">
            <RichTextEditor
              label="Comments"
              content={purchaseOrderDto.comments}
              onChange={(value) => {
                handleInputChange("comments", value);
              }}
              loading={isLoading}
            />
          </div>
          <EntityFileUpload
            entityId={selectedRow.id}
            entityType="PurchaseOrder"
            attachments={attachments}
            onAttachmentsChange={() => {
              if (selectedRow.id) {
                purchaseOrdersService
                  .fetchSinglePurchaseOrderRequest(selectedRow.id)
                  .then((purchaseOrderData) => {
                    setAttachments(purchaseOrderData.attachments);
                  })
                  .catch((error) => {
                    console.error("Error fetching purchase order data:", error);
                  });
              }
            }}
            disabled={isLoading || !selectedRow.id}
          />
        </div>
      </>
    );
  };

  const handleSearchTrigger = (searchTerm) => {
    if ((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;

    let pageNumber = pageNum;
    if (!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)) {
      setData([]); //clear existing data
      pageNumber = 1; //reset to first page
    }

    setTableSearch(searchTerm);
    fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);
  };

  return (
    <ColumnConfigProvider tableName="PurchaseOrders">
      <div className="listingScreen position-relative">
        <Table
          type="Purchase Order"
          rows={data || []}
          body={formBody()}
          searchHandler={handleSearch}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          setPageNum={setPageNum}
          perPageItems={perPageItems}
          setIsCreate={(val) => {
            handleCreate(val);
          }}
          loading={pageLoading}
          modalLoading={loading}
          isCreate={isCreate}
          pagination={pagination}
          handleSave={() => handleSave()}
          handleDelete={(selectedRow) => handleDelete(selectedRow)}
          handleCancel={() => handleCancel()}
          setIsEdit={setIsEdit}
          setSelectedRow={setSelectedRow}
          projectId={projectID}
          parentType="Project"
          handleAddRow={handleAddRow}
          handleCancelAddRow={handleCancelAddRow}
          setData={setData}
          triggerSearch={handleSearchTrigger}
        />
      </div>
    </ColumnConfigProvider>
  );
};

export default PurchaseOrders;
